import React, { useEffect, useState } from 'react';
import { Typography, Tooltip } from 'antd';
import { AimOutlined, CheckOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { ProjectStage, STAGE_GOALS } from '../../constants/stages';
import { stageGoals } from '../../services/api';
import { GoalItem as GoalItemType } from '../../types/project';

const { Title, Text } = Typography;

const Container = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 24px;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #e8e8e8;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const GoalsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const GoalItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GoalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const GoalCircle = styled.div<{ $completed?: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.$completed ? '#52c41a' : '#d9d9d9'};
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.$completed ? '#52c41a' : 'transparent'};

  .anticon {
    font-size: 12px;
    color: #fff;
  }
`;

const GoalTitle = styled(Text)`
  font-weight: 500;
  color: #1f2937;
  font-size: 15px;
  line-height: 1.6;
`;

const GoalDescription = styled(Text)`
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  padding-left: 32px;
`;

interface StageGoalsProps {
  stage: ProjectStage;
  projectId?: string;
  className?: string;
}

const StageGoals: React.FC<StageGoalsProps> = ({ stage, projectId, className }) => {
  const [goalsProgress, setGoalsProgress] = useState<GoalItemType[]>([]);
  const stageGoalsList = STAGE_GOALS[stage];

  useEffect(() => {
    const fetchGoalsProgress = async () => {
      if (!projectId) return;
      
      try {
        const response = await stageGoals.get(projectId, stage);
        setGoalsProgress(response.goals);
      } catch (error) {
        console.error('Failed to fetch goals progress:', error);
        // 如果获取失败，使用默认的未完成状态
        setGoalsProgress(stageGoalsList.map(goal => ({
          name: goal.title,
          isCompleted: false
        })));
      }
    };

    fetchGoalsProgress();
  }, [projectId, stage]);

  return (
    <Container className={className}>
      <GoalsList>
        {stageGoalsList.map((goal, index) => {
          const progressItem = goalsProgress.find(item => item.name === goal.title);
          const isCompleted = progressItem?.isCompleted || false;

          return (
            <GoalItem key={index}>
              <GoalHeader>
                <GoalCircle $completed={isCompleted}>
                  {isCompleted && <CheckOutlined />}
                </GoalCircle>
                <GoalTitle>{goal.title}</GoalTitle>
              </GoalHeader>
              <GoalDescription>{goal.description}</GoalDescription>
            </GoalItem>
          );
        })}
      </GoalsList>
    </Container>
  );
};

export default StageGoals; 